import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { API_CONFIG, ApiConfig } from '../../api-config';

/**
 * Shape de un item de la respuesta del endpoint
 * `/selects/type-classifications?countryId=...`.
 *
 * A diferencia de `FiscalSelectOption` (que tiene `value, label`
 * porque se usa como options de un `<select>`), este endpoint
 * devuelve TÍTULOS de secciones del sidebar (cada item representa
 * una sección como "Actividad de empresa", "Motivos", etc.). El
 * shape `code, title` matchea mejor esa semántica: el `code` es
 * la key de la sección y el `title` es el label customizado que
 * el backend quiere mostrar.
 */
export interface ClassificationTitleOption {
  code: string;
  title: string;
}

/**
 * Service para cargar los TÍTULOS de las secciones del tab
 * "Clasificaciones" del edit de país (todas las que existan en API).
 *
 * Endpoint:
 *   GET {apiUrl}/selects/type-classifications?countryId={countryId}
 *   → { success: true, data: [{ code, text }] }
 *
 * `code` = prefix del type_classification (clave de sección, ej. actividad_empresa).
 * No usar el code corto de catálogo (TC001). `text` = nombre visible.
 */
@Injectable({ providedIn: 'root' })
export class ClassificationTitlesSelectService {
  private readonly httpClient = inject(HttpClient);
  private readonly apiConfig = inject<ApiConfig>(API_CONFIG);

  /**
   * Cache por countryId: countryId -> options. Como el endpoint es
   * único, no necesitamos el cache anidado de `FiscalSelectsService`.
   */
  private readonly cache = new Map<string, ClassificationTitleOption[]>();

  /**
   * Endpoint único que sirve este service. Hardcodeado porque
   * `ClassificationTitlesSelectService` tiene un solo propósito
   * (cargar los títulos de las 10 secciones del tab classifications
   * del edit de país). Si en el futuro se quiere generalizar a
   * otros catálogos, se refactoriza a la firma de
   * `FiscalSelectsService` (con `endpointUrl` param).
   */
  private readonly endpointUrl = '/selects/type-classifications';

  /**
   * Carga los títulos de las 10 secciones del tab classifications
   * para un país dado. Mapea `{code, text}` del response a
   * `{code, title}` para esta vista.
   *
   * Si el `countryId` ya está en cache, devuelve `of(cached)` sin
   * hacer request. Si no, hace GET y guarda en cache.
   *
   * Devuelve `Observable<ClassificationTitleOption[]>` que emite
   * UNA vez con el array (vacío si la API devuelve data no-array
   * o falla). El componente se subscribe y actualiza sus signals.
   *
   * @param countryId id del país (UUID). Si es null/undefined,
   *                  devuelve `of([])` sin hacer request.
   */
  getTitles(
    countryId: string | null | undefined,
  ): Observable<ClassificationTitleOption[]> {
    if (!countryId) {
      return of([]);
    }

    const cached = this.cache.get(countryId);
    if (cached) {
      return of(cached);
    }

    return this.httpClient
      .get(
        `${this.apiConfig.apiUrl}${this.endpointUrl}?countryId=${countryId}`,
        {
          observe: 'response',
          responseType: 'text',
        },
      )
      .pipe(
        map((resp) => this.parseResponse(resp.body)),
        tap((options) => {
          // Guardamos incluso si es array vacío: si el endpoint devolvió
          // [] legítimo, no queremos re-fetchar cada vez.
          this.cache.set(countryId, options);
        }),
        catchError(() => {
          // Si falla, devolvemos [] silenciosamente. El componente
          // mantiene los labels hardcodeados como fallback. Acá NO
          // guardamos en cache para que un reintento futuro funcione.
          return of([]);
        }),
      );
  }

  /**
   * Parsea el body del response (que viene como `text` por la
   * convención del proyecto de hacer JSON.parse manual) y mapea
   * a `ClassificationTitleOption[]`.
   *
   * Convención de respuesta: `{ success: true, data: [{ code, text }] }`.
   * El campo `text` es el label customizado (lo que el backend
   * quiere mostrar); lo mapeamos a `title` para esta vista.
   *
   * Si el shape no matchea (no tiene `success: true` o `data`
   * no es array), devuelve `[]` sin tirar error.
   */
  private parseResponse(body: unknown): ClassificationTitleOption[] {
    if (typeof body !== 'string' || !body) return [];
    try {
      const parsed = JSON.parse(body) as {
        success?: boolean;
        data?: Array<{
          code?: string;
          prefix?: string;
          text?: string;
          title?: string;
          name?: string;
        }>;
      };
      if (!parsed || parsed.success !== true || !Array.isArray(parsed.data)) {
        return [];
      }
      return parsed.data.map((item) => ({
        code: item.prefix || item.code || '',
        title: item.text || item.title || item.name || '',
      }));
    } catch {
      return [];
    }
  }
}
